# Updating a job ad

Job ads can be modified under their existing URL after they’ve been posted.
While most fields can be freely updated, there are some [restrictions].

## Ad selection

With classic Job Posting, you cannot provide the same [`seekAdvertisementProductId`] that was initially used to post a job ad.
Display [ad selection] to the user in your update workflow.
This will present a distinct set of update products along with associated pricing and payment information.
Once an update product has been selected, its ID can be used to update the job ad:

```jsonc
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // ...
      "postingInstructions": {
        // Provide updated product ID if ad selection was displayed
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFf5CvE7z2UgDiG6nSMmPZKh"
        // ...
      }
    }
  }
}
```

In certain scenarios,
you may need to update a job ad without displaying ad selection to the hirer.
You can retain the existing ad product by [omitting] the [`seekAdvertisementProductId`] field entirely:

```jsonc
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // ...
      "postingInstructions": {
        // Omit `seekAdvertisementProductId` if ad selection was not displayed
        // ...
      }
    }
  }
}
```

[omitting]: /graphql/in-practice#optional-arguments

## Operations

### updatePostedPositionProfile

The [`updatePostedPositionProfile` mutation] updates a live job ad.
The [`PositionProfile`]’s existing fields will be replaced with the provided input fields.
You must provide the current values of any fields you don’t want to modify.
An exception is made for [`UpdatePostingInstructionInput.end`],
where omitting the field will preserve the existing end date.

For example, you could run this mutation to update a job ad with a new title:

```graphql
mutation ($input: UpdatePostedPositionProfileInput!) {
  updatePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```jsonc
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // Update the `positionTitle` from `Principal Developer` to `Lead Developer`
      "positionTitle": "Lead Developer",
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "positionFormattedDescriptions": [
        {
          "descriptionId": "SearchSummary",
          "content": "Experienced software developer wanted to lead an existing team."
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Line management required"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Need experience with cloud deployments"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Competitive benefits packages"
        },
        {
          "descriptionId": "AdvertisementDetails",
          "content": "<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>"
        }
      ],
      "offeredRemunerationPackage": {
        "basisCode": "Salaried",
        "ranges": {
          "minimumAmount": {
            "value": 100000,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 130000,
            "currency": "AUD"
          },
          "intervalCode": "Year"
        },
        "descriptions": ["Up to $130k excluding super"]
      },
      "seekAnzWorkTypeCode": "FullTime",
      "seekWorkArrangementCodes": ["Remote"],
      "seekVideo": {
        "url": "https://www.youtube.com/watch?v=Q3UvigPHCjI",
        "seekAnzPositionCode": "Above"
      },
      "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
      "positionLocation": "seekAnzPublicTest:location:seek:QVS62e6K",
      "postingInstructions": {
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFf5CvE7z2UgDiG6nSMmPZKh"
      }
    }
  }
}
```

```json
{
  "updatePostedPositionProfile": {
    "positionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
      }
    }
  }
}
```

You will receive a [`FORBIDDEN` error] if you try to update a job ad that has already been [closed].

## Events

### PositionProfileUpdated

A [`PositionProfileUpdated` event] is emitted when an existing job ad has been updated in the SEEK employment marketplace.
You will receive events for SEEK hirers you have a [`JobPosting` relationship] with at the time of updating.

The [`PositionProfileUpdated` event] is not emitted:

- When a new job ad is posted, instead a [`PositionProfileCreated` event] is emitted.
- When a job is closed, instead a [`PositionProfileClosed` event] is emitted.

In response to these events you can [request the most up-to-date values of the fields from the position profile].

[request the most up-to-date values of the fields from the position profile]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile

This is an example of a [webhook](/events/webhooks) body containing a `PositionProfileUpdated` event:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:U3LAePBniDDJb2PYkJgdWa",
      "type": "PositionProfileUpdated",
      "createDateTime": "2025-01-27T05:52:16.909Z",
      // This can be passed to the `positionProfile` query
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:2Hsmapbu9",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:D5Xa1QrddYJSSdnfVnXwHi",
  "url": "https://example.com/1/2/3",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

[`FORBIDDEN` error]: /graphql/error-responses#forbidden
[`JobPosting` relationship]: /auth/hirer-relationships
[`PositionProfile`]: /schema/named-type/PositionProfile
[`PositionProfileUpdated` event]: /schema/webhooks/PositionProfileUpdatedEvent
[`PositionProfileCreated` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`PositionProfileClosed` event]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad#positionprofileclosed
[`seekAdvertisementProductId`]: /schema/named-type/UpdatePostingInstructionInput#/field/seekAdvertisementProductId
[`updatePostedPositionProfile` mutation]: /schema/mutation/updatePostedPositionProfile
[`UpdatePostingInstructionInput.end`]: /schema/named-type/UpdatePostingInstructionInput#/field/end
[ad selection]: /use-cases/job-posting/ad-selection
[closed]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad
[restrictions]: /use-cases/job-posting/key-concepts#update-restrictions
